/// O vocabulário do plano de contas, dividido pela API e pela tela (ADR 0061).
///
/// Os seis grupos são fixos porque a cascata do DRE depende deles: a empresa cria, renomeia e
/// desativa contas dentro deles, mas não cria nem tira um grupo. O código de uma conta começa pelo
/// do grupo e tem no máximo mais dois níveis — "4", "4.1", "4.1.1".

export type AccountingGroupCode = '1' | '2' | '3' | '4' | '5' | '9';
export type AccountingDirection = 'INFLOW' | 'OUTFLOW';

export interface AccountingGroup {
  code: AccountingGroupCode;
  name: string;
  /// A linha do DRE em que o grupo entra. `null` = o grupo fica fora do resultado.
  dreLine: string | null;
  /// A direção que o dinheiro costuma ter numa conta do grupo. A outra não é recusada: entrada numa
  /// conta de despesa reduz a despesa. A tela só a marca como sentido contrário.
  normalDirections: readonly AccountingDirection[];
}

export const ACCOUNTING_GROUPS: readonly AccountingGroup[] = Object.freeze([
  { code: '1', name: 'Receitas', dreLine: 'Receita bruta', normalDirections: ['INFLOW'] },
  { code: '2', name: 'Deduções da receita', dreLine: 'Deduções', normalDirections: ['OUTFLOW'] },
  {
    code: '3',
    name: 'Custos dos serviços e produtos',
    dreLine: 'Custos',
    normalDirections: ['OUTFLOW'],
  },
  {
    code: '4',
    name: 'Despesas operacionais',
    dreLine: 'Despesas operacionais',
    normalDirections: ['OUTFLOW'],
  },
  {
    code: '5',
    name: 'Resultado financeiro',
    dreLine: 'Resultado financeiro',
    normalDirections: ['INFLOW', 'OUTFLOW'],
  },
  { code: '9', name: 'Fora do DRE', dreLine: null, normalDirections: ['INFLOW', 'OUTFLOW'] },
] satisfies AccountingGroup[]);

/// Dentro do Resultado financeiro, despesa e receita têm sentido próprio.
const DIRECTION_OVERRIDES: Readonly<Record<string, readonly AccountingDirection[]>> = Object.freeze(
  { '5.1': ['OUTFLOW'], '5.2': ['INFLOW'] },
);

/// O plano que a empresa pode adotar por um botão no primeiro acesso. Nada é criado ao contratar
/// (R1): sem o botão, o plano começa só com os grupos.
export const SUGGESTED_CHART: readonly (readonly [code: string, name: string])[] = Object.freeze([
  ['1.1', 'Serviços'],
  ['1.2', 'Produtos'],
  ['1.3', 'Pacotes'],
  ['1.4', 'Outras receitas operacionais'],
  ['1.4.1', 'Retenções por cancelamento'],
  ['2.1', 'ISS sobre serviços'],
  ['2.2', 'DAS — Simples Nacional'],
  ['2.3', 'Descontos concedidos'],
  ['2.4', 'Devoluções e cancelamentos de vendas'],
  ['3.1', 'Produtos para revenda'],
  ['3.2', 'Insumos e descartáveis'],
  ['4.1', 'Pessoal'],
  ['4.1.1', 'Salários'],
  ['4.1.2', 'Pró-labore'],
  ['4.1.3', 'Comissões de profissionais'],
  ['4.1.4', 'Bonificações'],
  ['4.1.5', 'Encargos (FGTS/INSS)'],
  ['4.1.6', 'Benefícios (vale-transporte e refeição)'],
  ['4.1.7', 'Comissões fora da folha'],
  ['4.2', 'Ocupação'],
  ['4.2.1', 'Aluguel'],
  ['4.2.2', 'Condomínio'],
  ['4.2.3', 'IPTU'],
  ['4.2.4', 'Segurança'],
  ['4.2.5', 'Limpeza e manutenção'],
  ['4.3', 'Utilidades'],
  ['4.3.1', 'Energia elétrica'],
  ['4.3.2', 'Água'],
  ['4.3.3', 'Telefone e internet'],
  ['4.4', 'Serviços de terceiros'],
  ['4.4.1', 'Contabilidade'],
  ['4.4.2', 'Sistema de gestão e tecnologia'],
  ['4.4.3', 'Marketing'],
  ['4.4.4', 'Comissões de autônomos e PJ'],
  ['4.5', 'Administrativas'],
  ['4.5.1', 'Transporte e deslocamento'],
  ['4.5.2', 'Outras despesas operacionais'],
  ['5.1', 'Despesas financeiras'],
  ['5.1.1', 'Tarifas bancárias'],
  ['5.1.2', 'Taxa de cartão'],
  ['5.1.3', 'Antecipação de recebíveis'],
  ['5.1.4', 'Juros, multas e IOF'],
  ['5.2', 'Receitas financeiras'],
  ['5.2.1', 'Rendimentos e juros recebidos'],
  ['9.1', 'Retiradas de sócios'],
  ['9.2', 'Distribuição de lucros'],
  ['9.3', 'Aportes de sócios'],
  ['9.4', 'Transferências entre contas próprias'],
  ['9.5', 'Aplicações em investimentos'],
  ['9.6', 'Resgates de investimentos'],
  ['9.7', 'Empréstimos tomados'],
  ['9.8', 'Amortização de empréstimos'],
  ['9.9', 'Reembolso a sócios'],
  ['9.10', 'Adiantamentos a profissionais'],
]);

const GROUP_CODES = new Set<string>(ACCOUNTING_GROUPS.map((group) => group.code));
const ACCOUNT_CODE = /^[0-9](\.[1-9][0-9]{0,2}){1,2}$/;

export function isAccountingGroupCode(code: string): code is AccountingGroupCode {
  return GROUP_CODES.has(code);
}

export function accountGroupOf(code: string): AccountingGroup | undefined {
  return ACCOUNTING_GROUPS.find((group) => group.code === code.split('.')[0]);
}

/// "4.1.1" → "4.1"; "4.1" → "4" (o grupo).
export function accountParentOf(code: string): string {
  return code.split('.').slice(0, -1).join('.');
}

/// 2 para "4.1", 3 para "4.1.1". O grupo é o nível 1 e não é conta.
export function accountLevelOf(code: string): number {
  return code.split('.').length;
}

export function normalDirectionsOf(code: string): readonly AccountingDirection[] {
  for (const [prefix, directions] of Object.entries(DIRECTION_OVERRIDES)) {
    if (code === prefix || code.startsWith(`${prefix}.`)) return directions;
  }
  return accountGroupOf(code)?.normalDirections ?? [];
}

/// A recusa em palavras de quem preenche, ou `null` quando o código serve.
export function validateAccountCode(code: string): string | null {
  if (!ACCOUNT_CODE.test(code) || !accountGroupOf(code)) {
    return 'Use o código do grupo seguido de um ou dois números, como 4.2 ou 4.2.6.';
  }
  return null;
}
